import moment from "moment";

import type { Primitives } from "@busbud/core-types";

import { OUTBOUND_LEG_INDEX } from "@app/constants/trip";
import { PASSENGER_CATEGORIES } from "@app/modules/checkout/constants";
import {
  getCartV2DiscountItem,
  getCartV2TripItem
} from "@app/modules/checkout/utils/cart-v2";
import type { PriceMode } from "@app/modules/results/constants/price-mode";
import { PRICE_MODE } from "@app/modules/results/constants/price-mode";
import type { NapiRoute } from "@app/types/napi-route-types";
import type {
  NapiSearchInfo,
  Passengers,
  PassengerDiscountsParams,
  DiscountType
} from "@app/types/napi-types";
import type {
  NapiV2CartItem,
  NapiV2TripItem
} from "@app/types/napi-v2-cart-types";
import type { NapiV2CartPassenger } from "@app/types/napi-v2-passengers-types";
import type { NapiV2Purchase } from "@app/types/napi-v2-purchase-types";
import type {
  PassengerBase,
  PassengerComplete,
  PassengerDiscounts,
  SearchInfo,
  SearchParams,
  SearchQueryWithDeprecated
} from "@app/types/search-types";

import type { PassengerType } from "../constants/passengers";
import type { RouteTimezones } from "../utils/timezones";

import { getPurchasePassengersCategoryCounts } from "./purchase/get-purchase-passengers-count";
import { getPurchaseTrip } from "./purchase/get-purchase-trip";
import { OUTBOUND_LEG_ID, RETURN_LEG_ID } from "./udm";
import {
  getLegOriginDestinationSegments,
  getOutboundLegOriginDestinationSegments
} from "./udm/get-leg-origin-destination-segments";
import { addDaysFormatted } from "../../app/utils/dates-without-moment";
import { DISCOUNT_TYPES } from "../constants/discount";
import { PASSENGER_TYPES } from "../constants/passengers";
import { UTC } from "../utils/timezones";
// We separate functions that don't need `moment` so they can be used
// on the client-side without additional bundle-size
// This will no-longer be necessary with Webpack 2 and tree-shaking
export * from "./search-client";
type DiscountKey = `${PassengerType}_${DiscountType}`;

/**
 * Create a {@link SearchInfo} object by parsing URL data (from path params and query)
 *
 * @param params
 * @param query
 * @param date
 * @returns
 */
export function fromUrlData(
  params: SearchParams,
  query: SearchQueryWithDeprecated,
  date?: moment.Moment | undefined
): SearchInfo {
  const outbound_date = getQueryOutboundDate(query, date);
  const return_date = getQueryReturnDate(query, date);

  return {
    origin: params.from,
    destination: params.to,
    outbound_date: outbound_date,
    return_date: return_date,
    // Different naming (`adults` instead of `adult`) for legacy reasons
    adult: decodeCount(query.adults),
    child: decodeCount(query.children),
    senior: decodeCount(query.seniors),
    adult_wheelchair: decodeCount(query.adult_wheelchair),
    child_wheelchair: decodeCount(query.child_wheelchair),
    senior_wheelchair: decodeCount(query.senior_wheelchair),
    adult_student: decodeCount(query.adult_student),
    child_student: decodeCount(query.child_student),
    senior_student: decodeCount(query.senior_student),
    child_ages: decodeAges(query.child_ages),
    senior_ages: decodeAges(query.senior_ages),
    child_wheelchair_ages: decodeAges(query.child_wheelchair_ages),
    senior_wheelchair_ages: decodeAges(query.senior_wheelchair_ages),
    child_student_ages: decodeAges(query.child_student_ages),
    senior_student_ages: decodeAges(query.senior_student_ages),
    discount_code: query.discount_code || query["discount-code"] || null,
    outbound_id: query.outbound_id || query.outbound_id || null,
    cart_id: query.cart_id || null,
    referral_cart_id: query.referral_cart_id || null,
    sort_key: query.sort_key || null,
    include_sold_out: null,
    leg_card_id: query.leg_card_id || null,
    outbound_leg_card_id_class_and_fare: query.outbound_lcf || null,
    return_leg_card_id_class_and_fare: query.return_lcf || null,
    vehicle_category: query.vehicle_category || undefined,
    price_mode: query.price_mode && decodePriceMode(query.price_mode)
  };
}

interface Cart {
  search_info: NapiSearchInfo;
  outbound_departure_id: string | null;
  id: string | null;
}

/**
 * Create a {@link SearchInfo} object from cart data
 *
 * @param cart
 * @returns
 */
export function fromCart(cart: Cart): SearchInfo {
  const search_info = cart.search_info;

  return {
    ..._fromNapiSearchInfo(search_info),
    outbound_id: cart.outbound_departure_id,
    cart_id: cart.id
  };
}

/**
 * Create a {@link SearchInfo} object from cart data
 * and removes the selected outbound state
 *
 * @param cart
 * @returns
 */
export function fromCartWithoutOutboundState(cart: Cart): SearchInfo {
  const search = fromCart(cart);
  return removeOutboundSearchState(search);
}

/**
 * Create a {@link SearchInfo} object from purchase data
 * and without the selected outbound state, including default values
 *
 * @param purchase
 * @returns
 */
export function fromPurchase(purchase: NapiV2Purchase): SearchInfo {
  const trip = getPurchaseTrip(purchase);
  const { origin_segment, destination_segment } =
    getOutboundLegOriginDestinationSegments(trip);

  return {
    ...getEmptySearchInfo(),
    origin: origin_segment.origin.city?.geohash ?? "",
    destination: destination_segment.destination.city?.geohash ?? "",
    outbound_date: "P1D" // Dynamically set current date + 1 on the search form at the time of the page load
  };
}

/**
 * Creates a {@link SearchInfo} object from purchase data for a return trip
 *
 * @param purchase
 * @returns
 */
export function getReturnSearchInfoFromPurchase(
  purchase: NapiV2Purchase /*, days_offset = 7*/
): SearchInfo {
  const trip = getPurchaseTrip(purchase);
  const { origin_segment, destination_segment } =
    getLegOriginDestinationSegments(trip, OUTBOUND_LEG_INDEX);

  const adults_passengers_count =
    getPurchasePassengersCategoryCounts(purchase).adult;

  return {
    ...getEmptySearchInfo(),
    origin: destination_segment.destination.city?.geohash ?? "",
    destination: origin_segment.origin.city?.geohash ?? "",
    adult: adults_passengers_count,
    outbound_date: addDaysFormatted(origin_segment.departure_time.timestamp, 7)
  };
}

/**
 * Create a {@link SearchInfo} object with default and empty values
 *
 * @returns
 */
function getEmptySearchInfo(): SearchInfo {
  return {
    origin: "",
    destination: "",
    outbound_date: null,
    outbound_id: null,
    cart_id: null,
    referral_cart_id: null,
    sort_key: null,
    include_sold_out: null,
    return_date: null,
    discount_code: null,
    adult_wheelchair: 0,
    child_ages: [],
    child_wheelchair: 0,
    child_wheelchair_ages: [],
    senior_ages: [],
    senior_wheelchair: 0,
    senior_wheelchair_ages: [],
    adult: 1,
    child: 0,
    senior: 0,
    adult_student: 0,
    child_student: 0,
    child_student_ages: [],
    senior_student: 0,
    senior_student_ages: []
  };
}

/**
 * Generates searchinfo discounts attributes from napi passengers
 *
 * @param passengers
 * @returns
 */
function _getSearchInfoDiscountAttributes(
  passengers: Passengers
): PassengerDiscounts {
  const discount_attributes = _initDiscountAttributes();
  const discounts = passengers.discounts || {};

  Object.entries(discounts).forEach(([passenger_type, discounts]) => {
    Object.entries(discounts).forEach(([discount_type, count]) => {
      const key = `${passenger_type}_${discount_type}` as DiscountKey;
      discount_attributes[key] = count;

      /**
       * discount_attributes[`${passenger_type}_${discount_type}_ages`] = ...
       * Should be set here but as of today (2020-09-04), NAPI does not return the age of the passengers to whom a discount applies.
       */
    });
  });

  return discount_attributes;
}

function _initDiscountAttributes(): PassengerDiscounts {
  return {
    adult_student: 0,
    child_student: 0,
    child_student_ages: [],
    senior_student: 0,
    senior_student_ages: []
  };
}

/**
 * Napi SearchInfo to Pubweb SeachInfo
 *
 * @param search_info
 * @returns
 */
function _fromNapiSearchInfo(search_info: NapiSearchInfo): SearchInfo {
  const passengers = search_info.passengers;

  return {
    origin: search_info.origin_city,
    destination: search_info.destination_city,
    outbound_date: search_info.outbound_date,
    return_date: search_info.return_date || null,
    adult: passengers.passenger_count.adult,
    child: passengers.passenger_count.child,
    senior: passengers.passenger_count.senior,
    adult_wheelchair: passengers.wheelchair_count.adult,
    child_wheelchair: passengers.wheelchair_count.child,
    senior_wheelchair: passengers.wheelchair_count.senior,
    child_ages: passengers.passenger_ages.child_ages,
    senior_ages: passengers.passenger_ages.senior_ages,
    child_wheelchair_ages: passengers.wheelchair_ages.child_ages,
    senior_wheelchair_ages: passengers.wheelchair_ages.senior_ages,
    discount_code: search_info.discount_code || null,
    // The properties below only make sense in the context of roundtrips,
    // to go back to the return leg selection page
    outbound_id: null,
    cart_id: null,
    referral_cart_id: null,
    sort_key: null,
    include_sold_out: null,
    ..._getSearchInfoDiscountAttributes(passengers)
  };
}

export type SearchStateForUrl = {
  outbound_date: string | null;
  return_date: string | null;
  passengers: PassengerComplete;
  discount_code: string | null;
};

export function getSearchQueryParametersFromReduxState(
  state: SearchStateForUrl,
  origin_geohash: Primitives.Geohash6,
  destination_geohash: Primitives.Geohash6
) {
  return {
    origin: origin_geohash,
    destination: destination_geohash,
    outbound_date: state.outbound_date,
    return_date: state.return_date,
    adult: state.passengers.adult,
    child: state.passengers.child,
    senior: state.passengers.senior,
    adult_wheelchair: state.passengers.adult_wheelchair,
    child_wheelchair: state.passengers.child_wheelchair,
    senior_wheelchair: state.passengers.senior_wheelchair,
    adult_student: state.passengers.adult_student,
    child_student: state.passengers.child_student,
    senior_student: state.passengers.senior_student,
    child_ages: state.passengers.child_ages,
    senior_ages: state.passengers.senior_ages,
    child_wheelchair_ages: state.passengers.child_wheelchair_ages,
    senior_wheelchair_ages: state.passengers.senior_wheelchair_ages,
    child_student_ages: state.passengers.child_student_ages,
    senior_student_ages: state.passengers.senior_student_ages,
    // Discount code is here so we can pass it from landing page,
    // through results, until checkout page.
    // => https://www.busbud.com/?discount_code=20XBB
    //    will apply the `20XBB` code to cart.
    discount_code: state.discount_code,
    sort_key: null,
    referral_cart_id: null,
    outbound_id: null,
    cart_id: null,
    include_sold_out: null
  };
}

/**
 * Support ISO 8601 Duration format for date query params
 * https://en.wikipedia.org/wiki/ISO_8601#Durations
 *
 * @param date_query
 * @param today
 * @returns
 */
function fromMaybeRelativeDate(
  date_query: string | string[] | undefined,
  today: moment.Moment
): string | undefined {
  if (typeof date_query !== "string") {
    return;
  }

  // Permissive check, but moment will handle badly-formatted durations for us
  const is_duration = date_query.startsWith("P");
  if (is_duration) {
    // Defaults to a duration of 0 if badly-formatted
    const duration = moment.duration(date_query);
    return today.clone().add(duration).format("YYYY-MM-DD");
  }

  return moment(date_query, "YYYY-MM-DD", true).isValid()
    ? date_query
    : undefined;
}

export function decodeCount(count_str: string | undefined): number {
  if (!count_str) {
    return 0;
  }

  const count = parseInt(count_str, 10) || 0;
  if (count < 0) {
    return 0;
  }
  return count;
}

export function decodeAges(ages_str: string | undefined): number[] {
  if (!(ages_str && ages_str.length)) {
    return [];
  }

  return ages_str.split(",").map(age => parseInt(age, 10));
}

export function decodePriceMode(price_mode: string | unknown): PriceMode {
  return Object.values(PRICE_MODE).includes(price_mode as PriceMode)
    ? (price_mode as PriceMode)
    : PRICE_MODE.BASE;
}

type IsReturnParams = Pick<
  SearchInfo,
  "outbound_id" | "cart_id" | "referral_cart_id" | "return_date"
>;

export function isReturn(search: IsReturnParams): boolean {
  return !!(
    (isRoundtrip(search) && search.outbound_id && search.cart_id) ||
    search.referral_cart_id
  );
}

export function isRoundtrip({
  return_date
}: Pick<SearchInfo, "return_date">): boolean {
  return !!return_date;
}

const DATE_FORMAT = "YYYY-MM-DD";

export function hasDiscountCode(search: SearchInfo): boolean {
  return !!search.discount_code;
}

interface RemoveOutboundSearchStateOptions {
  is_seat_selection_first: boolean;
}
/**
 * Convert a "return leg selection" search to its equivalent "outbound leg selection"
 *
 * @param search
 * @param options
 * @returns
 */
export function removeOutboundSearchState(
  search: SearchInfo,
  options: Partial<RemoveOutboundSearchStateOptions> = {}
): SearchInfo {
  const { is_seat_selection_first } = options;

  let clean_search = Object.assign({}, search, {
    outbound_id: null,
    cart_id: null,
    referral_cart_id: null,
    leg_card_id: null
  } as SearchInfo);

  if (is_seat_selection_first) {
    clean_search = resetAdults(clean_search);
  }

  return clean_search;
}

function resetAdults(search: SearchInfo): SearchInfo {
  return Object.assign({}, search, {
    adult: 1
  });
}

export function removeOutboundLegCardState(search: SearchInfo): SearchInfo {
  return Object.assign({}, search, {
    leg_card_id: null
  });
}

export function hasWheelchairs(search: SearchInfo): boolean {
  return (
    search.adult_wheelchair > 0 ||
    search.child_wheelchair > 0 ||
    search.senior_wheelchair > 0
  );
}

export function removeWheelchairs(search: SearchInfo): SearchInfo {
  return Object.assign({}, search, {
    adult_wheelchair: 0,
    child_wheelchair: 0,
    senior_wheelchair: 0,
    child_wheelchair_ages: [],
    senior_wheelchair_ages: []
  });
}

/**
 * Return total number of passengers
 *
 * @param search
 * @returns
 */
export function getPassengerCount(search: SearchInfo): number {
  return search.adult + search.child + search.senior;
}

export type PassengerDetailedCount = Pick<
  SearchInfo,
  "adult" | "senior" | "child"
>;

export function setOneWayLeavingToday(
  search: SearchInfo,
  timezones: RouteTimezones
): void {
  const { outbound_tz = UTC } = timezones;
  search.outbound_date = moment().tz(outbound_tz).format(DATE_FORMAT);
  search.return_date = null;
}

/**
 * Returns an object containing all discounts parameters for NAPI requests
 *
 * @param search
 * @returns
 */
export function getDiscountsQueryAttributes(
  search: SearchInfo
): PassengerDiscountsParams {
  const discounts_params: Record<string, number | number[]> = {};

  PASSENGER_TYPES.forEach(passenger_type => {
    DISCOUNT_TYPES.forEach(discount_type => {
      const nb_discounts =
        search[`${passenger_type}_${discount_type}` as DiscountKey];
      if (nb_discounts) {
        discounts_params[`${passenger_type}_${discount_type}_count`] =
          nb_discounts;

        if (passenger_type !== "adult") {
          discounts_params[`${passenger_type}_${discount_type}_ages`] =
            search[`${passenger_type}_${discount_type}_ages`] || [];
        }
      }
    });
  });

  return discounts_params;
}

export function extendSearchWithRoute(
  search: SearchInfo,
  route: NapiRoute
): SearchInfo {
  return {
    ...search,
    origin_city: {
      ...route.origin_city,
      city_id: route.origin_city.id,
      city_url: route.origin_city.legacy_url_form || ""
    },
    destination_city: {
      ...route.destination_city,
      city_id: route.destination_city.id,
      city_url: route.destination_city.legacy_url_form || ""
    }
  };
}

export function getQueryOutboundDate(
  query: SearchQueryWithDeprecated,
  date?: moment.Moment | undefined
): string | null {
  const today_date = date || moment();
  const outbound_date: string | string[] | undefined =
    query.outbound_date ||
    query["outbound-date"] ||
    query["departure-date"] ||
    undefined;
  return fromMaybeRelativeDate(outbound_date, today_date) || null;
}

export function getQueryReturnDate(
  query: SearchQueryWithDeprecated,
  date?: moment.Moment | undefined
): string | null {
  const today_date = date || moment();
  const return_date: string | string[] | undefined =
    query.return_date || query["return-date"] || undefined;
  return fromMaybeRelativeDate(return_date, today_date) || null;
}

/**
 * Create a {@link SearchInfo} object from cart data
 *
 * @param cart
 * @param cart_id
 * @param cart_items
 * @returns
 */
export function buildSearchfromCartV2(
  cart_id: string,
  cart_items: NapiV2CartItem[]
): SearchInfo {
  const trip_item = getCartV2TripItem(cart_items)!;
  const discount_item = getCartV2DiscountItem(cart_items);

  return {
    ..._fromCartTripItem(trip_item),
    outbound_id: trip_item.id,
    cart_id,
    discount_code: discount_item ? discount_item.code : null
  };
}

/**
 * Generates searchinfo discounts attributes from napi passengers
 *
 * @param passengers
 * @returns
 */
function _getSearchInfoDiscountAttributesV2(
  passengers: NapiV2CartPassenger[]
): PassengerDiscounts {
  const discount_attributes = _initDiscountAttributes();

  passengers.forEach(passenger => {
    passenger.discounts.forEach(discount => {
      let category: string = passenger.category;
      if (passenger.category === "youth") {
        category = "child";
      }
      discount_attributes[`${category}_${discount}` as DiscountKey] += 1;
    });
  });

  return discount_attributes;
}

function _getLegSegments(trip: NapiV2TripItem, leg_id: number) {
  const leg = trip.trip_legs.find(leg => leg.leg === leg_id);
  return leg?.segment_ids?.map(id =>
    trip.segments.find(segment => segment.id === id)
  );
}

function _getPassengersCount(passengers: NapiV2CartPassenger[]): PassengerBase {
  const search: PassengerBase = {
    adult: 1,
    child: 0,
    senior: 0,
    adult_wheelchair: 0,
    child_wheelchair: 0,
    senior_wheelchair: 0,
    child_ages: [],
    senior_ages: [],
    child_wheelchair_ages: [],
    senior_wheelchair_ages: []
  };

  PASSENGER_TYPES.forEach(passenger_type => {
    let category: string = passenger_type;
    if (passenger_type === PASSENGER_CATEGORIES.CHILD) {
      category = "youth";
    }

    const passengers_in_category = passengers.filter(
      passenger => passenger.category === category
    );
    const passengers_in_category_in_wheelchair = passengers_in_category.filter(
      passenger => !!passenger.wheelchair
    );

    search[passenger_type] = passengers_in_category.length;
    search[`${passenger_type}_wheelchair`] =
      passengers_in_category_in_wheelchair.length;

    if (passenger_type === PASSENGER_CATEGORIES.ADULT) {
      return;
    }

    if (passenger_type !== "adult") {
      search[`${passenger_type}_ages`] = passengers_in_category.map(
        passenger => passenger.age!
      );
      search[`${passenger_type}_wheelchair_ages`] =
        passengers_in_category_in_wheelchair.map(passenger => passenger.age!);
    }
  });

  return search;
}

/**
 * Cart trip item to Pubweb SearchInfo
 *
 * @param trip
 * @returns
 */
function _fromCartTripItem(trip: NapiV2TripItem): SearchInfo {
  const passengers = trip.passengers;
  const outbound_leg_segments = _getLegSegments(trip, OUTBOUND_LEG_ID) ?? [];
  const return_leg_segments = _getLegSegments(trip, RETURN_LEG_ID) ?? [];

  const origin = outbound_leg_segments[0]?.origin.city?.id ?? "";
  const last_outbound_segment =
    outbound_leg_segments[outbound_leg_segments.length - 1];
  const destination = last_outbound_segment?.destination.city?.id ?? "";
  const outbound_date =
    outbound_leg_segments[0]?.departure_time.timestamp.split("T")[0] ?? "";
  const return_date =
    return_leg_segments[0]?.departure_time.timestamp.split("T")[0] ?? null;

  return {
    origin,
    destination,
    outbound_date,
    return_date,
    discount_code: null,
    outbound_id: null,
    cart_id: null,
    referral_cart_id: null,
    sort_key: null,
    include_sold_out: null,
    ..._getPassengersCount(passengers),
    ..._getSearchInfoDiscountAttributesV2(passengers)
  };
}
